import React, { useEffect, useMemo, useState } from 'react';
import { translate } from '../../../utils/translation/translation';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  IconButton as MuiIconButton,
} from '@mui/material';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { translationsState } from '../../../atoms/translation/translation.atom';
import styles from './PdfPreviewDialog.module.scss';
import { useMutation } from '@tanstack/react-query';
import axios, { ErrorResponse } from '../../../libraries/axios/axios';
import { colors } from '../../../styles/colors';
import { modalOpenState } from '../../../atoms/layout/layout.atom';
import { FileContent } from '../../Input/AttachmentInput/AttachmentBox/AttachmentBox';
import { apiRoutes } from '../../../../config/api/apiRoutes';
import { saveAs } from 'file-saver';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import CloseIcon from '@mui/icons-material/Close';
import { SetValues, SubmitValues } from '../../../hooks/useForm/useForm';
import PasswordInput from '../../Input/PasswordInput/PasswordInput';
import { HttpError } from '../../../types/Api';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

export type PdfPreviewActionProps = {
  title?: string;
  subtitle?: string;
  isOpen?: boolean;
  onClose?: () => void;
  id?: string | string[];
  apiUrl?: string;
  readUrl?: string;
  readLabel?: string;
  onSuccess?: (data?: SetValues) => void;
  postValues?: SubmitValues;
  addFileName?: boolean;
};

export const PdfPreviewDialog = ({
  title,
  subtitle,
  isOpen = false,
  onClose,
  id,
  apiUrl,
  readUrl,
  readLabel,
  onSuccess,
  postValues,
  addFileName,
}: PdfPreviewActionProps) => {
  const translations = useRecoilValue(translationsState);
  const setIsModalOpen = useSetRecoilState(modalOpenState);

  const [fileContent, setFileContent] = useState<FileContent | null>(null);
  const [pdfContent, setPdfContent] = useState<Uint8Array | null>(null);
  const [numberOfPages, setNumberOfPages] = useState(0);

  const [passwordInput, setPasswordInput] = useState('');
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [passwordCallback, setPasswordCallback] = useState(null);

  const [scale, setScale] = useState(1.3);

  const [error, setError] = useState<string | undefined>();

  const { mutate: downloadFile, isPending: pendingDownload } = useMutation<
    { data: FileContent },
    HttpError
  >({
    mutationFn: () => {
      if (postValues) {
        return axios.post(apiUrl ?? '', postValues);
      }

      if (Array.isArray(id)) {
        return axios.post(apiUrl ?? '', { ids: id });
      }

      if (apiUrl) {
        return axios.get(apiUrl.replace(':id', id?.toString() ?? ''));
      }

      return axios.get(
        apiRoutes.assets.downloadFile.replace(':id', id?.toString() ?? ''),
      );
    },
    onSuccess: (response) => {
      setFileContent(response.data);
    },
    onError: (response) => {
      setError(
        translate(
          translations,
          response?.response?.data?.message?.toString() ?? '',
        ),
      );
    },
  });

  const { mutate: completeRead } = useMutation<
    { data: FileContent },
    ErrorResponse
  >({
    mutationFn: () => {
      return axios.post(readUrl?.replace(':id', id?.toString() ?? '') ?? '');
    },
    onSuccess: () => {
      handleClose();
      onSuccess?.();
    },
  });

  useEffect(() => {
    if (!fileContent || !fileContent.fileBuffer) {
      return;
    }

    setPdfContent(new Uint8Array(fileContent.fileBuffer.data));
  }, [fileContent]);

  useEffect(() => {
    setIsModalOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      downloadFile();
    }
  }, [isOpen]);

  const file = useMemo(() => {
    if (!pdfContent || !fileContent) {
      return null;
    }

    return new Blob([pdfContent], {
      type: fileContent.contentType || 'application/pdf',
    });
  }, [pdfContent, fileContent?.contentType]);

  const handleDownloadClick = () => {
    if (!fileContent || !fileContent.fileBuffer) {
      return;
    }

    saveAs(
      new Blob([new Uint8Array(fileContent.fileBuffer.data)], {
        type: fileContent.contentType,
      }),
      fileContent.filename,
    );
  };

  const handleClose = () => {
    setIsModalOpen(false);
    onClose?.();
  };

  const handlePassword = (callback: any) => {
    setShowPasswordInput(true);
    setPasswordCallback(() => callback);
  };

  const submitPassword = () => {
    if (passwordCallback) {
      // @ts-ignore
      passwordCallback(passwordInput);
      setShowPasswordInput(false);
    }
  };

  const getTitle = () => {
    if (addFileName) {
      return `${title ?? ''} ${fileContent?.filename ?? ''}`;
    }

    return title ?? '';
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="responsive-dialog-title"
      className={styles.pdfPreviewDialog}
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle id="responsive-dialog-title" className={styles.dialogTitle}>
        {getTitle()}
        <MuiIconButton onClick={handleClose} size="small">
          <CloseIcon fontSize={'small'} />
        </MuiIconButton>
      </DialogTitle>
      {subtitle && (
        <DialogTitle
          id="responsive-dialog-subtitle"
          className={styles.dialogSubtitle}
          style={{ color: colors.textSubtitleColor }}
        >
          {subtitle ?? <>&nbsp;</>}
        </DialogTitle>
      )}
      <Box className={styles.zoomIcons}>
        <IconButton
          onClick={() => {
            if (scale > 100) {
              return;
            }

            setScale((prev) => prev + 0.1);
          }}
          size="medium"
        >
          <ZoomInIcon fontSize="medium" />
        </IconButton>
        <IconButton
          onClick={() => {
            if (scale < 0.1) {
              return;
            }

            setScale((prev) => prev - 0.1);
          }}
          size="medium"
        >
          <ZoomOutIcon fontSize="medium" />
        </IconButton>
      </Box>
      <DialogContent
        className={styles.dialogContent}
        style={{ color: colors.textSubtitleColor }}
      >
        {showPasswordInput && (
          <form onSubmit={submitPassword} className={styles.passwordContainer}>
            <PasswordInput
              label={translate(
                translations,
                'PDF_PREVIEW_DIALOG.PASSWORD_PLACEHOLDER',
              )}
              name={'password'}
              value={passwordInput}
              onChange={(_, value) => setPasswordInput(value)}
              autoFocus
            />
            <Button onClick={submitPassword} variant={'contained'}>
              {translate(translations, 'PDF_PREVIEW_DIALOG.CONFIRM_PASSWORD')}
            </Button>
          </form>
        )}
        {file && !pendingDownload && (
          <>
            <Document
              file={file}
              onLoadSuccess={(e) => setNumberOfPages(e.numPages)}
              className={styles.pdfPreview}
              onPassword={handlePassword}
              loading={
                showPasswordInput ? null : (
                  <Box
                    className={styles.loader}
                    style={{
                      backgroundColor: colors.loaderBackgroundColor,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )
              }
            >
              {[...Array(numberOfPages)].map((_, pageIndex) => (
                <div
                  key={pageIndex}
                  className={styles.page}
                  style={{ borderColor: colors.dividerBorderColor }}
                >
                  <Page pageNumber={pageIndex + 1} scale={scale} />
                </div>
              ))}
            </Document>
            {readUrl && !showPasswordInput && (
              <Button
                variant={'contained'}
                disabled={pendingDownload}
                className={styles.readButton}
                onClick={() => completeRead()}
              >
                {readLabel}
              </Button>
            )}
          </>
        )}
        {pendingDownload && (
          <Box
            className={styles.loader}
            style={{
              backgroundColor: colors.loaderBackgroundColor,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        {error && (
          <Alert variant={'standard'} severity={'error'} color={'error'}>
            {error}
          </Alert>
        )}
        <Button onClick={handleClose} variant={'outlined'}>
          {translate(translations, 'PDF_PREVIEW_DIALOG.CANCEL')}
        </Button>
        <Button
          onClick={() => handleDownloadClick()}
          variant={'contained'}
          disabled={pendingDownload}
        >
          {translate(translations, 'PDF_PREVIEW_DIALOG.DOWNLOAD')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PdfPreviewDialog;
